import {
  CalendarEvent,
  FoodBookingModel,
  ServiceBookingModel,
  ViewEmployeeAbsence,
  getEntityType,
} from '@xeetr/types/models'
import { getAbsence, getBooking, getClosedSocietyHostBooking } from '@xeetr/types/utils'

export type EventClickTarget =
  | { kind: 'none' }
  | { kind: 'booking'; booking: FoodBookingModel | ServiceBookingModel }
  | { kind: 'absence'; absence: ViewEmployeeAbsence }
  | { kind: 'foodEvent'; id: string }
  | { kind: 'monthBookingDrilldown'; date: Date | string }

/**
 * Resolves what a calendar event click should open. `closedSociety` background events route to
 * their host booking's edit sidebar; `buffer` events are inert. Month-view booking cells are
 * aggregates, so they drill into day view instead of opening a (possibly wrong) booking.
 */
export const getEventClickTarget = (
  data: CalendarEvent | undefined,
  isMonthView: boolean
): EventClickTarget => {
  if (!data) return { kind: 'none' }
  if (data.type === 'buffer') return { kind: 'none' }

  if (data.type === 'closedSociety') {
    const booking = getClosedSocietyHostBooking(data)
    return booking ? { kind: 'booking', booking } : { kind: 'none' }
  }

  switch (getEntityType(data)) {
    case 'absence': {
      const absence = getAbsence(data)
      return absence ? { kind: 'absence', absence } : { kind: 'none' }
    }
    case 'foodEvent':
      return { kind: 'foodEvent', id: data.id }
    case 'booking': {
      if (isMonthView) return { kind: 'monthBookingDrilldown', date: data.start }
      const booking = getBooking(data)
      return booking ? { kind: 'booking', booking } : { kind: 'none' }
    }
    default:
      return { kind: 'none' }
  }
}

/** Calendar event types that must not get the recurring-series badge. */
export const isBadgelessEventType = (type: string | undefined): boolean =>
  type === 'buffer' || type === 'closedSociety'
